import { Permission } from 'shared'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { useFDCNotificationsContext } from '../../FDCNotificationsContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import {
  EditFdcNotificationInput,
  useEditFdcNotificationMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useCallback } from 'react'
import { CommonFormItemsValues } from './components/RenderFormItems/components/CommonFormItems/types'
import { TwoDimensionalGroupFormItemsValues } from './components/RenderFormItems/components/TwoDimensionalGroupFormItems/types'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { useGlobalState } from 'context/GlobalStateContext'
import RenderFormItems from './components/RenderFormItems'
import { errorHandler } from 'hooks/errorHandler'

export function useEditFn() {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { recipients, refetch } = useFDCNotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useEditFdcNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editFDCNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const {
        every,
        byConcurrenceLevel,
        byApprovalGroup,
        byConcurrenceGroup,
        byConcurrenceLevelAndApprovalGroup,
        byConcurrenceLevelAndConcurrenceGroup,
      } = recipients(event)
      return {
        render: <RenderFormItems />,
        initialValues: {
          recipients: every ? toLabeledValues(every) : [],
          concurrenceLevels: byConcurrenceLevel.map(
            ({ concurrenceLevel, recipients }) => ({
              concurrenceLevelId: concurrenceLevel.id,
              recipients: toLabeledValues(recipients),
            })
          ),
          approvalGroup: {
            type: 'approvalGroup',
            approvalGroups: byApprovalGroup.map(
              ({ approvalGroup, recipients }) => ({
                approvalGroupId: approvalGroup.id,
                recipients: toLabeledValues(recipients),
              })
            ),
            approvalGroupConcurrenceLevels:
              byConcurrenceLevelAndApprovalGroup.map(
                ({ approvalGroup, concurrenceLevel, recipients }) => ({
                  approvalGroupId: approvalGroup.id,
                  concurrenceLevelId: concurrenceLevel.id,
                  recipients: toLabeledValues(recipients),
                })
              ),
          },
          concurrenceGroup: {
            type: 'concurrenceGroup',
            concurrenceGroups: byConcurrenceGroup.map(
              ({ concurrenceGroup, recipients }) => ({
                concurrenceGroupId: concurrenceGroup.id,
                recipients: toLabeledValues(recipients),
              })
            ),
            concurrenceGroupConcurrenceLevels:
              byConcurrenceLevelAndConcurrenceGroup.map(
                ({ concurrenceGroup, concurrenceLevel, recipients }) => ({
                  concurrenceGroupId: concurrenceGroup.id,
                  concurrenceLevelId: concurrenceLevel.id,
                  recipients: toLabeledValues(recipients),
                })
              ),
          },
        },
        permission: Permission.EditFDCNotification,
        onSave: async ({
          recipients,
          concurrenceLevels,
          approvalGroup,
          concurrenceGroup,
        }) => {
          if (!projectId) return errorHandler('projectId is undefined')
          const input: EditFdcNotificationInput = {
            notificationEventId: event.id,
            projectId,
            every: fromLabeledValues(recipients),
            byConcurrenceLevel: concurrenceLevels.map(
              ({ recipients, ...rest }) => ({
                ...rest,
                recipients: fromLabeledValues(recipients),
              })
            ),
          }
          if (approvalGroup?.type === 'approvalGroup') {
            input.byApprovalGroup = approvalGroup.approvalGroups.map(
              ({ recipients, ...rest }) => ({
                ...rest,
                recipients: fromLabeledValues(recipients),
              })
            )
            input.byConcurrenceLevelAndApprovalGroup =
              approvalGroup.approvalGroupConcurrenceLevels.map(
                ({ recipients, ...rest }) => ({
                  ...rest,
                  recipients: fromLabeledValues(recipients),
                })
              )
          }
          if (concurrenceGroup?.type === 'concurrenceGroup') {
            input.byConcurrenceGroup = concurrenceGroup.concurrenceGroups.map(
              ({ recipients, ...rest }) => ({
                ...rest,
                recipients: fromLabeledValues(recipients),
              })
            )
            input.byConcurrenceLevelAndConcurrenceGroup =
              concurrenceGroup.concurrenceGroupConcurrenceLevels.map(
                ({ recipients, ...rest }) => ({
                  ...rest,
                  recipients: fromLabeledValues(recipients),
                })
              )
          }
          await edit({ variables: { editFdcNotificationInput: input } })
          refetch()
        },
        onClear: async () => {
          if (!projectId) return errorHandler('projectId is not defined')
          await edit({
            variables: {
              editFdcNotificationInput: {
                notificationEventId: event.id,
                projectId,
                byApprovalGroup: [],
                byConcurrenceGroup: [],
                byConcurrenceLevel: [],
                byConcurrenceLevelAndApprovalGroup: [],
                byConcurrenceLevelAndConcurrenceGroup: [],
                every: { userIds: [], nonUserEmailIds: [] },
              },
            },
          })
        },
      }
    },
    [edit, recipients, projectId, refetch, toLabeledValues, fromLabeledValues]
  )
}

interface InitialValues extends CommonFormItemsValues {
  /** will only be present in any approval */
  approvalGroup?: TwoDimensionalGroupFormItemsValues
  /** will only be present in any concurrence */
  concurrenceGroup?: TwoDimensionalGroupFormItemsValues
}
